import { Link } from "@tanstack/react-router";
import { Plus, ShoppingCart, Trash2 } from "lucide-react";
import { useState } from "react";
import { OrderCreateDialog } from "@/components/orders/OrderCreateDialog";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { EnumSelect } from "@/components/shared/enum-select";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { RowActions } from "@/components/shared/row-actions";
import { SearchInput } from "@/components/shared/search-input";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { DEFAULT_LIMIT, useDataGrid } from "@/hooks/use-data-grid";
import { type Order, useDeleteOrder, useOrders } from "@/hooks/use-orders";
import { ORDER_STATUS_OPTIONS } from "@/lib/enums";
import { formatCurrency, formatDate } from "@/lib/format";

export function OrdersTable() {
  const grid = useDataGrid({ defaultSort: "createdAt" });
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Order | null>(null);

  const { data, isLoading, isError, error, refetch } = useOrders(grid.query, grid.pageRequest);
  const deleteOrder = useDeleteOrder();

  const orders = data?.data ?? [];
  const total = data?.total ?? 0;
  const hasNext = data?.hasNext ?? false;
  const hasPrev = data?.hasPrev ?? false;

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    deleteOrder.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
  }

  const toolbar = (
    <div className="flex items-center gap-4">
      <SearchInput
        value={grid.searchInput}
        onValueChange={grid.setSearch}
        placeholder="Search by customer..."
        className="flex-1 max-w-sm"
      />
      <EnumSelect
        value={grid.status}
        onValueChange={grid.setStatus}
        options={ORDER_STATUS_OPTIONS}
        allOption={{ label: "All statuses" }}
        placeholder="All statuses"
        className="w-[160px]"
        aria-label="Filter by status"
      />
      <Button size="sm" className="ml-auto" onClick={() => setCreateOpen(true)}>
        <Plus className="h-4 w-4 mr-1" />
        New order
      </Button>
    </div>
  );

  if (isError) {
    return (
      <div className="space-y-4">
        {toolbar}
        <ErrorState message={error?.message ?? "Failed to load orders"} onRetry={() => refetch()} />
        <OrderCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {toolbar}

      <DataTable
        headers={[
          // customerName (joined) + items/total (aggregates) aren't keyset-sortable.
          { label: "Customer" },
          { label: "Status", sortKey: "status" },
          { label: "Items", className: "hidden md:table-cell text-right" },
          { label: "Total", className: "text-right" },
          { label: "Created", className: "hidden md:table-cell", sortKey: "createdAt" },
          { label: "", className: "w-12" },
        ]}
        sort={grid.sort}
        sortDir={grid.sortDir}
        onSort={grid.toggleSort}
        loading={isLoading}
        empty={
          <EmptyState
            icon={ShoppingCart}
            title="No orders found"
            description="Try adjusting your search or filters."
          />
        }
      >
        {orders.map((order) => (
          <tr
            key={order.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3 font-medium">
              <Link
                to="/dashboard/orders/$orderId"
                params={{ orderId: order.id }}
                className="hover:text-primary transition-colors"
              >
                {order.customerName}
              </Link>
            </td>
            <td className="px-4 py-3">
              <StatusBadge status={order.status} />
            </td>
            <td className="hidden md:table-cell px-4 py-3 text-right text-muted-foreground">
              {order.itemCount}
            </td>
            <td className="px-4 py-3 text-right font-medium">{formatCurrency(order.totalCents)}</td>
            <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
              {formatDate(order.createdAt)}
            </td>
            <td className="px-4 py-3 text-right">
              <RowActions
                actions={[
                  {
                    label: "Delete",
                    icon: Trash2,
                    destructive: true,
                    onSelect: () => setDeleteTarget(order),
                  },
                ]}
              />
            </td>
          </tr>
        ))}
      </DataTable>

      <CursorPagination
        total={total}
        page={grid.page}
        limit={DEFAULT_LIMIT}
        hasNext={hasNext}
        hasPrev={hasPrev}
        onNext={() => grid.goNext(data?.nextCursor ?? null)}
        onPrev={() => grid.goPrev(data?.prevCursor ?? null)}
        loading={isLoading}
        itemLabel="orders"
      />

      <OrderCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete order"
        description={`Delete this order for ${deleteTarget?.customerName ?? "this customer"}? Its line items will be removed too. This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        loading={deleteOrder.isPending}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
